import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Drawer, Row, Table, Tag, Typography, message } from 'antd'
import felContingencySrc from './felContingencySrc'
import LoadMoreButton from '../../components/LoadMoreButton'
import Utils from '../../utils/Utils'

const { Title } = Typography

const DIAS_LIMITE_SAT = 5

const getVencimiento = sellDate => {
  if (!sellDate) return null
  const limite = new Date(sellDate)
  limite.setDate(limite.getDate() + DIAS_LIMITE_SAT)
  return limite
}

const getDiasRestantes = sellDate => {
  const limite = getVencimiento(sellDate)
  if (!limite) return null
  const ahora = new Date()
  const diffMs = limite.getTime() - ahora.getTime()
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
}

const VencimientoTag = ({ sellDate }) => {
  const dias = getDiasRestantes(sellDate)
  if (dias === null) return <span>-</span>
  if (dias < 0) return <Tag color="red">{`Vencida hace ${Math.abs(dias)} día(s)`}</Tag>
  if (dias <= 1) return <Tag color="red">{'Vence hoy/mañana'}</Tag>
  if (dias <= 2) return <Tag color="orange">{`Vence en ${dias} días`}</Tag>
  return <Tag color="green">{`Vence en ${dias} días`}</Tag>
}

const ESTADO_LABELS = {
  pending: { label: 'Pendiente', color: 'gold' },
  resolved: { label: 'Resuelta', color: 'green' },
  // "discarded" en certificación es siempre por el plazo de 5 días de SAT
  // (lo descarta el worker solo, confirmado con Digifact). En anulación no
  // hay esa misma regla confirmada todavía — si una anulación está
  // descartada fue una decisión manual, no algo que el sistema detectó, así
  // que la etiqueta no debe afirmar una razón que no confirmamos.
  discarded_certify: { label: 'Descartada (pasó el plazo SAT)', color: 'default' },
  discarded_cancel: { label: 'Descartada', color: 'default' },
}

const EstadoTag = ({ status, operation }) => {
  const key = status === 'discarded' ? `discarded_${operation}` : status
  const estado = ESTADO_LABELS[key] || { label: status, color: 'default' }
  return <Tag color={estado.color}>{estado.label}</Tag>
}

export const FelContingencyIndex = () => {
  const [loading, setLoading] = useState(false)
  const [dataSource, setDataSource] = useState([])
  const [nextPage, setNextPage] = useState('')
  const [existMoreInfo, setExistMoreInfo] = useState(false)
  const [visible, setVisible] = useState(false)
  const [editDataDrawer, setEditDataDrawer] = useState(null)
  const [totalPendientes, setTotalPendientes] = useState(0)

  useEffect(() => {
    loadData()
    felContingencySrc.read(1, 'pending').then(result => {
      setTotalPendientes(result?.data?.total || 0)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getContingencies = dataSrc => {
    if (dataSrc !== 'Too Many Attempts.') {
      let _dataSrc = dataSrc.data.data || []
      return _dataSrc.map(d => ({
        key: d.id,
        id: d.id,
        sell_id: d.sell_id,
        operation: d.operation,
        status: d.status,
        attempts: d.attempts,
        last_error: d.last_error,
        last_attempted_at: d.last_attempted_at,
        created_at: d.created_at,
        sell_date: d.sell?.date,
        access_number: d.sell?.access_number,
        sell_total: d.sell?.total,
        store_name: d.sell?.store?.name,
        company_name: d.sell?.store?.company?.name,
        client_name: d.sell?.client?.name,
      }))
    } else {
      message.error('Se excedió el límite de peticiones, espera y recarga la aplicación')
    }
  }

  const loadData = () => {
    setLoading(true)
    felContingencySrc
      .read(20)
      .then(result => {
        setDataSource(getContingencies(result))
        setNextPage(result.data.next_page_url)
        setExistMoreInfo(result.data.next_page_url !== null)
        setLoading(false)
      })
      .catch(err => {
        console.log(err)
        message.error('No se ha podido cargar la información')
      })
  }

  const moreInfo = () => {
    if (existMoreInfo) {
      setLoading(true)
      Utils.getNextPage(nextPage)
        .then(response => {
          setDataSource(dataSource.concat(getContingencies(response)))
          setNextPage(response.data.next_page_url)
          setExistMoreInfo(response.data.next_page_url !== null)
          setLoading(false)
        })
        .catch(err => {
          console.log(err)
          message.error('No se ha podido cargar la información')
        })
    }
  }

  const verDetalle = row => {
    setEditDataDrawer(row)
    setVisible(true)
  }

  const onClose = () => {
    setVisible(false)
  }

  const columns = [
    {
      title: 'Venta',
      dataIndex: 'sell_id',
      key: 'sell_id',
      render: text => <span>{`#${text}`}</span>,
    },
    {
      title: 'Empresa',
      dataIndex: 'company_name',
      key: 'company_name',
    },
    {
      title: 'Tienda',
      dataIndex: 'store_name',
      key: 'store_name',
    },
    {
      title: 'Fecha de venta',
      dataIndex: 'sell_date',
      key: 'sell_date',
    },
    {
      title: 'Operación',
      dataIndex: 'operation',
      key: 'operation',
      render: op => <span>{op === 'certify' ? 'Certificación' : 'Anulación'}</span>,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status, row) => <EstadoTag status={status} operation={row.operation} />,
    },
    {
      title: 'Intentos',
      dataIndex: 'attempts',
      key: 'attempts',
    },
    {
      title: 'Vencimiento (5 días SAT)',
      dataIndex: 'sell_date',
      key: 'vencimiento',
      render: (sellDate, row) => (row.status === 'pending' ? <VencimientoTag sellDate={sellDate} /> : <span>-</span>),
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'actions',
      align: 'right',
      render: (id, row) => (
        <Button type="link" onClick={() => verDetalle(row)}>
          Ver detalle
        </Button>
      ),
    },
  ]

  return (
    <>
      <Row className={'section-space-field'}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <Alert
            type={totalPendientes > 0 ? 'warning' : 'success'}
            showIcon
            message={totalPendientes > 0 ? `${totalPendientes} venta(s) en contingencia pendiente(s) de certificar` : 'No hay contingencias pendientes'}
          />
        </Col>
      </Row>
      <Row className={'section-space-field'}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <Card className={'card-border-radius margin-top-15'}>
            <Row>
              <Col xs={24} sm={24} md={24} lg={24}>
                <Table loading={loading} className={'CustomTableClass'} dataSource={dataSource} columns={columns} pagination={false} rowKey="id" />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <LoadMoreButton handlerButton={moreInfo} moreInfo={existMoreInfo} />
      <Drawer placement="right" closable={false} onClose={onClose} visible={visible} width={500}>
        <Title level={3}>{`Contingencia — Venta #${editDataDrawer?.sell_id || ''}`}</Title>
        <p><strong>{'Empresa: '}</strong>{editDataDrawer?.company_name}</p>
        <p><strong>{'Tienda: '}</strong>{editDataDrawer?.store_name}</p>
        <p><strong>{'Cliente: '}</strong>{editDataDrawer?.client_name}</p>
        <p><strong>{'Fecha de venta: '}</strong>{editDataDrawer?.sell_date}</p>
        <p><strong>{'Total: '}</strong>{editDataDrawer?.sell_total}</p>
        <p><strong>{'Número de acceso: '}</strong>{editDataDrawer?.access_number || '-'}</p>
        <p><strong>{'Operación: '}</strong>{editDataDrawer?.operation === 'certify' ? 'Certificación' : 'Anulación'}</p>
        <p><strong>{'Estado: '}</strong><EstadoTag status={editDataDrawer?.status} operation={editDataDrawer?.operation} /></p>
        <p><strong>{'Intentos: '}</strong>{editDataDrawer?.attempts}</p>
        <p><strong>{'Último intento: '}</strong>{editDataDrawer?.last_attempted_at || '-'}</p>
        {editDataDrawer?.status === 'pending' && (
          <p><strong>{'Vencimiento (5 días SAT): '}</strong><VencimientoTag sellDate={editDataDrawer?.sell_date} /></p>
        )}
        <p><strong>{'Último error de Digifact: '}</strong></p>
        <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fafafa', padding: 8, borderRadius: 4 }}>
          {editDataDrawer?.last_error || 'Sin error registrado'}
        </p>
        <Row>
          <Col xs={24} sm={24} md={24} lg={24}>
            <div className="text-right">
              <Button type="link" className="cancel-button" onClick={onClose}>
                Cerrar
              </Button>
            </div>
          </Col>
        </Row>
      </Drawer>
    </>
  )
}
